import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  /**
   * Jumps the slideshow to a specific slide. Lets controls outside the gallery (e.g. a
   * product-details button) select a slide by index without reaching into the slideshow
   * ref directly.
   * @param {number|string|{id: string}} input - index or slide id
   * @param {Event} [event]
   */
  select(input, event) {
    this.slideshow?.select(input, event);
  }

  /**
   * Selects a slide and scrolls the gallery back into view. For controls that live below
   * the fold (e.g. a product-details button clicked after scrolling past the gallery), so
   * the slide change is actually visible instead of happening off-screen.
   * @param {number|string|{id: string}} input - index or slide id
   * @param {Event} [event]
   */
  jumpTo(input, event) {
    this.select(input, event);

    // Scroll the window directly instead of scrollIntoView(): this gallery is
    // position: sticky and sits beside a long product-details column that can contain
    // its own scrollable/scroll-snap carousels (flavour swatches, thumbnails), and
    // ancestor-walking scrollIntoView can be swallowed or redirected to the wrong
    // scroller in that situation (notably in Safari). Scrolling `window` explicitly,
    // from a fresh measurement after the slide change, is unambiguous.
    requestAnimationFrame(() => {
      const top = window.scrollY + this.getBoundingClientRect().top - 16;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    });
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
